import React, { memo } from "react";

import { Button, Icon } from "common/components-v2";
import commonKeysConfig from "common/config/common-keys.config";
import { IconCollection } from "common/config/media.config";
import { ButtonType, ButtonVariant } from "common/enums";
import useCustomTranslation from "common/hooks/useCustomTranslation";
import { useAppSelector } from "common/redux/core/root.reducer";
import { toSafeObjectKey } from "common/utils/object";

import { ClientOptions } from "modules/home/enums/create-engagement/create-engagement.enum";
import { Client } from "modules/home/models/home-common.model";
import labelCollection from "../../config/langauge-keys.config";
import { getClientInitials } from "../../util/getClientInitials.util";

import "./ClientListItem.scss";

type ClientListItemProps = {
  clientData: Client;
  onOptionClick: (option: ClientOptions, data: Client) => void;
};

const ClientListItem = React.forwardRef<HTMLDivElement, ClientListItemProps>(
  ({ clientData, onOptionClick }, ref) => {
    const { t } = useCustomTranslation(["home", "common"]);

    const { id, name, clientCode, users } = clientData;
    const { isSuperAdmin, id: currentUserid } = useAppSelector(
      (state) => state.user
    );
    const { clientLogos } = useAppSelector((state) => state.home);

    const clientLogo = clientLogos?.[toSafeObjectKey(id)] || "";
    const isCurrentUserPartOfClient = users?.find(
      (user) => user?.userId === currentUserid
    );

    return (
      <div className="client-list-item-v2" ref={ref} data-client-id={id}>
        <div className="client-list-item-v2__left">
          <div className="client-logo">
            {clientLogo ? (
              <img
                src={clientLogo ?? ""}
                className="profile-img"
                alt="Client Icon"
              />
            ) : (
              <p>
                <span>{getClientInitials(name)} </span>
              </p>
            )}
          </div>

          <div className="client-detail">
            <p className="client-detail__name">{name}</p>
            <p className="client-detail__id">
              {t(labelCollection.id)}: {clientCode}
            </p>
          </div>
        </div>

        <div className="client-list-item-v2__right">
          <Button
            type={ButtonType.BUTTON}
            variant={ButtonVariant.PRIMARY}
            size="small"
            onClick={() => onOptionClick(ClientOptions.VIEW, clientData)}
            disabled={!isCurrentUserPartOfClient && !isSuperAdmin}
            aria-label={t(commonKeysConfig.view)}
          >
            {t(commonKeysConfig.view)}

            <Icon
              src={IconCollection.IC_ARROW_RIGHT}
              className="chevron-right-icon"
              stroke="none"
              strokeWidth=".01rem"
            />
          </Button>

          {isSuperAdmin ? (
            <>
              <Button
                variant="secondary"
                onClick={() => onOptionClick(ClientOptions.EDIT, clientData)}
              >
                {t(commonKeysConfig.edit)}
              </Button>

              <Button
                className="download-feedback"
                variant="secondary"
                onClick={() =>
                  onOptionClick(ClientOptions.DOWNLOAD_FEEDBACK, clientData)
                }
              >
                {t(labelCollection.downloadFeedback)}
              </Button>
            </>
          ) : null}
        </div>
      </div>
    );
  }
);

export default memo(ClientListItem);
